import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, ArrowLeft, X, Upload, Plus, Trash2 } from 'lucide-react';
import api from '../services/api';
import {
  getVenueById,
  createVenue,
  updateVenue,
  uploadPhoto,
  addAmenity,
  removeAmenity,
  setOperatingHours,
} from '../services/venueService';
import { getDayName } from '../utils/formatters';

const DAYS = [0, 1, 2, 3, 4, 5, 6];

const defaultHours = {
  monday: { windows: [{ open: '08:00', close: '22:00' }], isOpen: true },
  tuesday: { windows: [{ open: '08:00', close: '22:00' }], isOpen: true },
  wednesday: { windows: [{ open: '08:00', close: '22:00' }], isOpen: true },
  thursday: { windows: [{ open: '08:00', close: '22:00' }], isOpen: true },
  friday: { windows: [{ open: '08:00', close: '23:00' }], isOpen: true },
  saturday: { windows: [{ open: '08:00', close: '23:00' }], isOpen: true },
  sunday: { windows: [{ open: '09:00', close: '21:00' }], isOpen: true },
};

function cloneDefaultHours() {
  return Object.fromEntries(Object.entries(defaultHours).map(([day, value]) => [
    day,
    { ...value, windows: value.windows.map((window) => ({ ...window })) },
  ]));
}

function suggestClosingTime(openTime) {
  const [hours, minutes] = openTime.split(':').map(Number);
  const closeMinutes = Math.min((hours * 60) + minutes + 120, (24 * 60) - 1);
  return `${String(Math.floor(closeMinutes / 60)).padStart(2, '0')}:${String(closeMinutes % 60).padStart(2, '0')}`;
}

function normalizeOperatingHours(value) {
  const normalized = cloneDefaultHours();
  if (Array.isArray(value)) {
    const grouped = value.reduce((result, entry) => {
      const day = String(entry.dayOfWeek || '').toLowerCase();
      if (!normalized[day]) return result;
      if (!result[day]) result[day] = { isOpen: true, windows: [] };
      if (entry.isClosed) {
        result[day] = { isOpen: false, windows: [] };
      } else if (result[day].isOpen) {
        if (entry.is24Hours) result[day].is24Hours = true;
        result[day].windows.push({ open: entry.openTime, close: entry.closeTime });
      }
      return result;
    }, {});

    Object.entries(grouped).forEach(([day, schedule]) => {
      normalized[day] = schedule.isOpen && schedule.windows.length === 0
        ? { isOpen: true, windows: [{ ...defaultHours[day].windows[0] }] }
        : schedule;
    });
    return normalized;
  }

  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([day, schedule]) => {
      if (!normalized[day]) return;
      normalized[day] = schedule.windows
        ? { isOpen: schedule.isOpen !== false, is24Hours: !!schedule.is24Hours, windows: schedule.windows.map((window) => ({ ...window })) }
        : {
            isOpen: schedule.isOpen !== false,
            is24Hours: !!schedule.is24Hours,
            windows: [{
              open: schedule.open || schedule.openTime || defaultHours[day].windows[0].open,
              close: schedule.close || schedule.closeTime || defaultHours[day].windows[0].close,
            }],
          };
    });
  }
  return normalized;
}

export default function VenueForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    province: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    pricePerHour: '',
    bookingAdvanceDays: '8',
    maxBookingHoursPerTransaction: '',
    supportsQrPhPayments: true,
    supportsGCashPayments: true,
    supportsCreditCardPayments: true,
  });
  const [amenities, setAmenities] = useState([]);
  const [newAmenity, setNewAmenity] = useState('');
  const [hours, setHours] = useState(cloneDefaultHours);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      loadVenue();
    }
  }, [id]);

  async function loadVenue() {
    try {
      const data = await getVenueById(id);
      const venue = data.venue || data;
      setFormData({
        name: venue.name || '',
        description: venue.description || '',
        address: venue.address || '',
        city: venue.city || '',
        province: venue.state || '',
        zipCode: venue.zipCode || '',
        latitude: venue.latitude?.toString() || '',
        longitude: venue.longitude?.toString() || '',
        pricePerHour: venue.pricePerHour?.toString() || '',
        bookingAdvanceDays: (venue.bookingAdvanceDays ?? 8).toString(),
        maxBookingHoursPerTransaction: venue.maxBookingHoursPerTransaction?.toString() || '',
        supportsQrPhPayments: venue.supportsQrPhPayments !== false,
        supportsGCashPayments: venue.supportsGCashPayments !== false,
        supportsCreditCardPayments: venue.supportsCreditCardPayments !== false,
      });
      setAmenities(venue.amenities || []);
      setPhotos(venue.photos || []);
      if (venue.operatingHours) setHours(normalizeOperatingHours(venue.operatingHours));
    } catch (err) {
      toast.error('Failed to load venue');
      navigate('/venues');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  function toggleDay(day, isOpen) {
    setErrors((prev) => ({ ...prev, operatingHours: '' }));
    setHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen,
        windows: isOpen && prev[day].windows.length === 0
          ? [{ ...defaultHours[day].windows[0] }]
          : prev[day].windows,
      },
    }));
  }

  function handleWindowChange(day, index, field, value) {
    setErrors((prev) => ({ ...prev, operatingHours: '' }));
    setHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        windows: prev[day].windows.map((window, windowIndex) =>
          windowIndex === index ? { ...window, [field]: value } : window),
      },
    }));
  }

  function addWindow(day) {
    setErrors((prev) => ({ ...prev, operatingHours: '' }));
    setHours((prev) => {
      const previousWindow = prev[day].windows[prev[day].windows.length - 1];
      const suggestedOpen = previousWindow?.close || '14:00';
      return {
        ...prev,
        [day]: {
          ...prev[day],
          windows: [...prev[day].windows, { open: suggestedOpen, close: suggestClosingTime(suggestedOpen) }],
        },
      };
    });
  }

  function removeWindow(day, index) {
    setErrors((prev) => ({ ...prev, operatingHours: '' }));
    setHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        windows: prev[day].windows.filter((_, windowIndex) => windowIndex !== index),
      },
    }));
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const data = await uploadPhoto(id || 'new', file);
      setPhotos((prev) => [...prev, data.imageUrl || data.url || data.photo]);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  }

  async function handleDeletePhoto(photoUrl) {
    const venueId = id;
    if (!venueId || venueId === 'new') return;
    try {
      await api.delete(`/venues/${venueId}/photos`, { params: { imageUrl: photoUrl } });
      setPhotos((prev) => prev.filter((p) => p !== photoUrl));
      toast.success('Photo deleted');
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  }

  async function handleAddAmenity() {
    if (!newAmenity.trim()) return;
    if (isEdit) {
      try {
        const data = await addAmenity(id, newAmenity.trim());
        setAmenities((prev) => [...prev, data.amenity || { name: newAmenity.trim() }]);
      } catch {
        toast.error('Failed to add amenity');
      }
    } else {
      setAmenities((prev) => [...prev, { name: newAmenity.trim() }]);
    }
    setNewAmenity('');
  }

  async function handleRemoveAmenity(amenityId, index) {
    if (isEdit && amenityId) {
      try {
        await removeAmenity(id, amenityId);
      } catch {
        toast.error('Failed to remove amenity');
      }
    }
    setAmenities((prev) => prev.filter((_, i) => i !== index));
  }

  function validate() {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.city.trim()) errs.city = 'City is required';
    if (!formData.pricePerHour || parseFloat(formData.pricePerHour) <= 0) {
      errs.pricePerHour = 'Valid price is required';
    }
    const bookingAdvanceDays = Number(formData.bookingAdvanceDays);
    if (!Number.isInteger(bookingAdvanceDays) || bookingAdvanceDays < 0 || bookingAdvanceDays > 365) {
      errs.bookingAdvanceDays = 'Enter a whole number between 0 and 365';
    }
    if (formData.maxBookingHoursPerTransaction !== '') {
      const maximumHours = Number(formData.maxBookingHoursPerTransaction);
      if (!Number.isInteger(maximumHours) || maximumHours < 1 || maximumHours > 24) {
        errs.maxBookingHoursPerTransaction = 'Enter a whole number between 1 and 24';
      }
    }

    const hasLatitude = formData.latitude.trim() !== '';
    const hasLongitude = formData.longitude.trim() !== '';
    if (hasLatitude !== hasLongitude) {
      errs.coordinates = 'Enter both latitude and longitude';
    } else if (hasLatitude) {
      const latitude = Number(formData.latitude);
      const longitude = Number(formData.longitude);
      if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
        errs.latitude = 'Latitude must be between -90 and 90';
      }
      if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
        errs.longitude = 'Longitude must be between -180 and 180';
      }
    }

    for (const [day, schedule] of Object.entries(hours)) {
      if (!schedule.isOpen || schedule.is24Hours) continue;
      const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
      if (schedule.windows.length === 0) {
        errs.operatingHours = `${dayLabel} needs at least one operating window.`;
        break;
      }

      const sortedWindows = [...schedule.windows].sort((first, second) => first.open.localeCompare(second.open));
      if (sortedWindows.some((window) => !window.open || !window.close || window.close === window.open)) {
        errs.operatingHours = `${dayLabel} has an invalid operating window. Opening and closing times must be different.`;
        break;
      }
      if (sortedWindows.some((window) => window.close < window.open) && sortedWindows.length > 1) {
        errs.operatingHours = `${dayLabel} can only have one operating window when it closes the next day.`;
        break;
      }
      if (sortedWindows.some((window, index) => index > 0 && window.open < sortedWindows[index - 1].close)) {
        errs.operatingHours = `${dayLabel} has overlapping operating windows.`;
        break;
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    if (!formData.supportsQrPhPayments && !formData.supportsGCashPayments) {
      toast.error('Select QR Ph or GCash for online bookings. Credit cards are optional in the app.');
      return;
    }

    setSaving(true);
    try {
      const { province, latitude, longitude, ...venueDetails } = formData;
      const payload = {
        ...venueDetails,
        state: province,
        pricePerHour: parseFloat(formData.pricePerHour),
        bookingAdvanceDays: Number(formData.bookingAdvanceDays),
        maxBookingHoursPerTransaction: formData.maxBookingHoursPerTransaction === ''
          ? 0
          : Number(formData.maxBookingHoursPerTransaction),
        amenities: amenities.map((a) => (typeof a === 'string' ? { name: a } : a)),
      };
      if (latitude.trim() !== '') {
        payload.latitude = Number(latitude);
        payload.longitude = Number(longitude);
      }

      if (isEdit) {
        await updateVenue(id, payload);
        await setOperatingHours(id, hours);
        toast.success('Venue saved successfully');
      } else {
        const data = await createVenue(payload);
        const venueId = data.venue?._id || data.venue?.id || data._id || data.id;
        if (venueId) {
          await setOperatingHours(venueId, hours);
        }
        toast.success('Venue created successfully');
        navigate(`/venues/${venueId}/courts`);
        return;
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save venue';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">
            {isEdit ? 'Edit Venue' : 'New Venue'}
          </h1>
          <p className="text-sm text-[#6B7280]">
            {isEdit ? 'Update your venue details' : 'Add a new FREAC venue'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-[#1F2937]">Basic Information</h2>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#6B7280]">Venue Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.name ? 'border-[#D46C4E]' : ''}`}
                placeholder="e.g., Downtown Sports Club"
              />
              {errors.name && <p className="mt-1 text-sm text-[#D46C4E]">{errors.name}</p>}
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#6B7280]">Description</label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="input-field mt-1"
                placeholder="Describe your venue..."
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#6B7280]">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="input-field mt-1"
                placeholder="123 Main Street"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6B7280]">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.city ? 'border-[#D46C4E]' : ''}`}
                placeholder="City"
              />
              {errors.city && <p className="mt-1 text-sm text-[#D46C4E]">{errors.city}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6B7280]">Province</label>
              <input
                type="text"
                name="province"
                value={formData.province}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.province ? 'border-[#D46C4E]' : ''}`}
                placeholder="Province (optional)"
              />
              {errors.province && <p className="mt-1 text-sm text-[#D46C4E]">{errors.province}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6B7280]">Zip Code</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                className="input-field mt-1"
                placeholder="ZIP Code"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6B7280]">Latitude</label>
              <input
                type="number"
                step="any"
                min="-90"
                max="90"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.latitude || errors.coordinates ? 'border-[#D46C4E]' : ''}`}
                placeholder="e.g., 14.5995"
              />
              {errors.latitude && <p className="mt-1 text-sm text-[#D46C4E]">{errors.latitude}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6B7280]">Longitude</label>
              <input
                type="number"
                step="any"
                min="-180"
                max="180"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.longitude || errors.coordinates ? 'border-[#D46C4E]' : ''}`}
                placeholder="e.g., 120.9842"
              />
              {errors.longitude && <p className="mt-1 text-sm text-[#D46C4E]">{errors.longitude}</p>}
              {errors.coordinates && <p className="mt-1 text-sm text-[#D46C4E]">{errors.coordinates}</p>}
            </div>

            <p className="sm:col-span-2 -mt-3 text-xs text-[#6B7280]">
              Coordinates are optional, but both are needed to show this venue on the player map.
            </p>

            <div>
              <label className="block text-sm font-medium text-[#6B7280]">Price Per Hour (PHP)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                name="pricePerHour"
                value={formData.pricePerHour}
                onChange={handleChange}
                className={`input-field mt-1 ${errors.pricePerHour ? 'border-[#D46C4E]' : ''}`}
                placeholder="25.00"
              />
              {errors.pricePerHour && <p className="mt-1 text-sm text-[#D46C4E]">{errors.pricePerHour}</p>}
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1F2937]">Booking Window</h2>
            <p className="mt-1 text-sm text-[#64748B]">
              Limit how far ahead players can book this venue.
            </p>
          </div>
          <div className="max-w-sm">
            <label className="block text-sm font-medium text-[#6B7280]">
              Maximum days bookable in advance
            </label>
            <input
              type="number"
              name="bookingAdvanceDays"
              min="0"
              max="365"
              step="1"
              value={formData.bookingAdvanceDays}
              onChange={handleChange}
              className={`input-field mt-1 ${errors.bookingAdvanceDays ? 'border-[#D46C4E]' : ''}`}
            />
            {errors.bookingAdvanceDays && (
              <p className="mt-1 text-sm text-[#D46C4E]">{errors.bookingAdvanceDays}</p>
            )}
          </div>
          <p className="text-xs text-[#6B7280]">
            Example: 8 lets players book today through 8 calendar days ahead. Set 0 for same-day bookings only.
          </p>
          <div className="max-w-sm pt-3">
            <label className="block text-sm font-medium text-[#6B7280]">
              Maximum hours per booking transaction
            </label>
            <input
              type="number"
              name="maxBookingHoursPerTransaction"
              min="1"
              max="24"
              step="1"
              value={formData.maxBookingHoursPerTransaction}
              onChange={handleChange}
              className={`input-field mt-1 ${errors.maxBookingHoursPerTransaction ? 'border-[#D46C4E]' : ''}`}
              placeholder="No limit"
            />
            {errors.maxBookingHoursPerTransaction && (
              <p className="mt-1 text-sm text-[#D46C4E]">{errors.maxBookingHoursPerTransaction}</p>
            )}
          </div>
          <p className="text-xs text-[#6B7280]">
            Applies to the combined duration of all courts and time slots in one checkout. Leave blank for no limit.
          </p>
        </div>

        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-[#1F2937]">Online Payment Methods</h2>
            <p className="mt-1 text-sm text-[#64748B]">Choose the payment options players can use for this venue. Keep QR Ph or GCash enabled for online bookings.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] p-4">
              <input type="checkbox" name="supportsQrPhPayments" checked={formData.supportsQrPhPayments} onChange={handleChange} className="h-4 w-4" />
              <span className="font-medium text-[#1F2937]">QR Ph</span>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] p-4">
              <input type="checkbox" name="supportsGCashPayments" checked={formData.supportsGCashPayments} onChange={handleChange} className="h-4 w-4" />
              <span className="font-medium text-[#1F2937]">GCash</span>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] p-4">
              <input type="checkbox" name="supportsCreditCardPayments" checked={formData.supportsCreditCardPayments} onChange={handleChange} className="h-4 w-4" />
              <span><span className="block font-medium text-[#1F2937]">Credit / debit card</span><span className="text-xs text-[#64748B]">Local and international Visa / Mastercard · app only</span></span>
            </label>
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[#1F2937]">Photos</h2>
          <div className="flex flex-wrap gap-3">
            {photos.map((photo, idx) => (
              <div key={idx} className="relative group h-24 w-24 rounded-lg overflow-hidden border border-[#E2E8F0]">
                <img src={photo} alt="" className="h-full w-full object-cover" />
                {id && id !== 'new' && (
                  <button
                    onClick={() => handleDeletePhoto(photo)}
                    className="absolute top-1 right-1 p-1 bg-[#264D59]/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#D46C4E]"
                  >
                    <X className="h-3 w-3 text-white" />
                  </button>
                )}
              </div>
            ))}
            <label className="h-24 w-24 flex items-center justify-center rounded-lg border-2 border-dashed border-[#E2E8F0] cursor-pointer hover:border-[#264D59] hover:bg-[#F1F5F9] transition-colors">
              {uploading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#264D59] border-t-transparent" />
              ) : (
                <Upload className="h-6 w-6 text-[#6B7280]" />
              )}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            </label>
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[#1F2937]">Amenities</h2>
          <div className="flex flex-wrap gap-2">
            {amenities.map((amenity, idx) => (
              <span
                key={amenity._id || idx}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[30px] text-sm bg-[#F1F5F9] text-[#1F2937] border border-[#E2E8F0]"
              >
                {amenity.name}
                <button type="button" onClick={() => handleRemoveAmenity(amenity._id, idx)}>
                  <X className="h-3.5 w-3.5 text-[#6B7280] hover:text-[#D46C4E]" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newAmenity}
              onChange={(e) => setNewAmenity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAmenity())}
              className="input-field flex-1"
              placeholder="Add amenity (e.g., Lighting, Parking)"
            />
            <button type="button" onClick={handleAddAmenity} className="btn-outline">
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-[#1F2937]">Operating Hours</h2>
            <p className="mt-1 text-sm text-[#64748B]">
              Add multiple windows when the venue closes and reopens during the day. A closing time earlier than the opening time means the venue closes the next day (for example, 8:00 AM to 3:00 AM).
            </p>
          </div>
          {errors.operatingHours && (
            <p className="rounded-xl bg-[#FDF1EB] px-4 py-3 text-sm text-[#D46C4E]">{errors.operatingHours}</p>
          )}
          <div className="divide-y divide-[#E2E8F0]">
            {DAYS.map((day) => {
              const dayKey = getDayName(day).toLowerCase();
              const dayLabel = getDayName(day);
              const schedule = hours[dayKey];
              return (
                <div key={day} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between gap-4">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-[#6B7280]">
                      <input
                        type="checkbox"
                        checked={schedule?.isOpen}
                        onChange={(e) => toggleDay(dayKey, e.target.checked)}
                        className="rounded border-[#E2E8F0] text-[#264D59] focus:ring-[#264D59]"
                      />
                      {dayLabel}
                    </label>
                    {schedule?.isOpen && !schedule.is24Hours && (
                      <button
                        type="button"
                        onClick={() => addWindow(dayKey)}
                        disabled={schedule.windows[schedule.windows.length - 1]?.close === '23:59'}
                        className="inline-flex items-center text-xs font-semibold text-[#264D59] hover:text-[#56B7AC] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Add hours
                      </button>
                    )}
                  </div>
                  {schedule?.isOpen ? (
                    <div className="mt-3 space-y-2 sm:ml-7">
                      <label className="inline-flex items-center gap-2 text-sm font-medium text-[#264D59]">
                        <input type="checkbox" checked={!!schedule.is24Hours}
                          aria-label={`${dayLabel} open 24 hours`}
                          onChange={(e) => setHours((prev) => ({ ...prev, [dayKey]: {
                            ...prev[dayKey], is24Hours: e.target.checked,
                            windows: e.target.checked ? prev[dayKey].windows : prev[dayKey].windows.map(w =>
                              w.open === w.close ? { ...defaultHours[dayKey].windows[0] } : w),
                          } }))} />Open 24 hours
                      </label>
                      {schedule.is24Hours && <p className="text-xs text-[#64748B]">Midnight to midnight — includes all 24 hourly slots. Existing bookings and blocks still apply.</p>}
                      {!schedule.is24Hours && schedule.windows.map((window, index) => (
                        <div key={`${dayKey}-${index}`} className="flex flex-wrap items-center gap-2">
                          <input
                            type="time"
                            aria-label={`${dayLabel} window ${index + 1} opening time`}
                            value={window.open}
                            onChange={(e) => handleWindowChange(dayKey, index, 'open', e.target.value)}
                            className="input-field !h-11 w-32"
                          />
                          <span className="text-sm text-[#6B7280]">to</span>
                          <input
                            type="time"
                            aria-label={`${dayLabel} window ${index + 1} closing time`}
                            value={window.close}
                            onChange={(e) => handleWindowChange(dayKey, index, 'close', e.target.value)}
                            className="input-field !h-11 w-32"
                          />
                          {schedule.windows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeWindow(dayKey, index)}
                              className="rounded-lg p-2 text-[#D46C4E] hover:bg-[#FDF1EB]"
                              aria-label={`Remove ${dayLabel} window ${index + 1}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : <p className="mt-2 text-sm text-[#94A3B8] sm:ml-7">Closed all day</p>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pb-8">
          <button type="button" onClick={() => navigate(-1)} className="btn-outline">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Saving...
              </span>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEdit ? 'Save Changes' : 'Create Venue'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
